import { CircularProgress, TableRow } from '@mui/material';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import moment from 'moment/moment';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Case } from '../../../../shared/api/hscan/case';
import { CdDeliveryPayment } from '../../../../shared/api/hscan/hospital';
import { useCaseByIds } from '../../../../shared/hooks/caseHook';
import { HscanTableCell } from '../../common/components/SortableTable';
import { handleCellStyle } from '../../common/components/SortableTable/RenderTable';
import {
  BlueStatusMessage,
  GreenStatusMessage,
  GreyStatusMessage,
} from '../../common/components/styledComponents';
import { useModalityLabel } from '../../hooks/modalityHook';

const CdDeliveryTableRow = styled(TableRow)`
  background: #f8f9fd;
`;

export interface CdDeliveryPaymentTableProps {
  payment: CdDeliveryPayment;
}
export const CasesTable = ({ payment }: CdDeliveryPaymentTableProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table.cdTable',
  });
  const modalityLabel = useModalityLabel();
  const { data: cases, isFetching } = useCaseByIds({ ids: payment.caseIds });

  const columnHelper = useMemo(() => createColumnHelper<Case>(), []);
  const cellOptions = useMemo(
    () => [
      columnHelper.display({
        id: '__PLACEHOLDER__',
        meta: {
          cellStyle: { background: 'white', borderBottom: { style: 'none' } },
          headerStyle: { background: 'white', borderBottom: { style: 'none' } },
        },
      }),
      columnHelper.accessor('studyDate', {
        header: t('studyDate') as string,
        cell: ({ getValue }) => moment(getValue()).format('YYYY.MM.DD'),
      }),
      columnHelper.accessor('institutionName', {
        header: t('hospitalName') as string,
      }),
      columnHelper.accessor('modality', {
        header: t('modality') as string,
        cell: ({ getValue }) => modalityLabel(getValue()),
      }),
      columnHelper.accessor('studyDescription', {
        header: t('studyDescription') as string,
      }),
      columnHelper.display({
        id: '__EMPTY_FIELD_1__',
      }),
      columnHelper.display({
        id: '__EMPTY_FIELD_2__',
      }),
      columnHelper.display({
        id: '__EMPTY_FIELD_3__',
      }),
    ],
    [columnHelper, modalityLabel, t],
  );
  const table = useReactTable({
    data: cases ?? [],
    columns: cellOptions,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <>
      {cases ? (
        <>
          <CdDeliveryTableRow>
            {table.getLeafHeaders().map(header => (
              <HscanTableCell
                key={header?.id}
                {...handleCellStyle(header.column.columnDef.meta?.headerStyle)}
                align="center">
                {
                  flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  ) as React.ReactNode
                }
              </HscanTableCell>
            ))}
          </CdDeliveryTableRow>
          {table.getRowModel().rows.map(row => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map(cell => (
                <HscanTableCell
                  key={cell.id}
                  {...handleCellStyle(cell.column.columnDef.meta?.cellStyle)}
                  align="center">
                  {
                    flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    ) as React.ReactNode
                  }
                </HscanTableCell>
              ))}
            </TableRow>
          ))}
        </>
      ) : (
        isFetching && (
          <TableRow>
            <HscanTableCell />
            <HscanTableCell colSpan={cellOptions.length} align="center">
              <CircularProgress />
            </HscanTableCell>
          </TableRow>
        )
      )}
    </>
  );
};

interface CdDeliveryMailingOrderProps {
  paymentMailStatus: CdDeliveryPayment['mailStatus'];
}
export const CdDeliveryMailingOrder = ({
  paymentMailStatus,
}: CdDeliveryMailingOrderProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'features.mypage.payments.table.mailStatus',
  });

  if (paymentMailStatus === 'ORDER_MAILED') {
    return <BlueStatusMessage>{t('mailed')}</BlueStatusMessage>;
  } else if (paymentMailStatus === 'ORDER_MAIL_CANCELLED') {
    return <GreyStatusMessage>{t('mailCanceled')}</GreyStatusMessage>;
  } else {
    return <GreenStatusMessage>{t('waitForMail')}</GreenStatusMessage>;
  }
};
